import React from 'react'

import { useWorkflowSchema } from 'data/hooks/workflows/workflows'
import { assertIsDefined } from 'data/utils/ts_utils'
import { FlowLine } from 'features/workflows/FlowLine'
import { ItemIcon } from 'features/workflows/ItemIcon'
import { WorkflowChain } from 'features/workflows/WorkflowChain'
import { WorkflowChainContextProvider } from 'features/workflows/WorkflowChainContext'
import { WorkflowItemWrapper } from 'features/workflows/WorkflowItemWrapper'

import { Box } from 'ui/components/Box'
import { Body } from 'ui/components/Text'

type WorkflowConditionalProps = {
    node: WorkflowConditionalConfig
    showType?: boolean
}

export function WorkflowConditionalNode({ node }: WorkflowConditionalProps) {
    return (
        <Box noShrink flex column center maxWidth="full">
            <WorkflowItemWrapper item={node} flex center maxWidth="200px" width="200px">
                <WorkflowConditional node={node} />
            </WorkflowItemWrapper>
            <Box height="l" position="relative" flex center justifyContent="center">
                <Box
                    position="absolute"
                    width="3xs"
                    height="full"
                    borderColor="border"
                    borderStyle="dashed"
                    borderWidth={1}
                    style={{
                        margin: 'auto',
                        top: 0,
                        bottom: 0,
                    }}
                />
            </Box>
            <WorkflowConditionalChain node={node} />
        </Box>
    )
}

export const WorkflowConditional: React.FC<WorkflowConditionalProps> = ({ node, showType }) => {
    const { data: schema } = useWorkflowSchema()

    const nodeType = schema?.nodes?.find((type) => type.id === node.kind)
    assertIsDefined(nodeType)

    return (
        <>
            <ItemIcon icon={nodeType.icon!} iconStyle={nodeType.icon_style} mr="m" />

            <Box flex column alignItems="flex-start" fontWeight="bodyRegular">
                {showType && (
                    <Box fontSize="bodyXs" color="textWeakest">
                        Conditional
                    </Box>
                )}
                <Box flex center>
                    {nodeType.name}
                </Box>
            </Box>
        </>
    )
}

type WorkflowConditionalChainProps = React.ComponentPropsWithoutRef<typeof Box> & {
    node: WorkflowConditionalConfig
}

const WorkflowConditionalChain: React.FC<WorkflowConditionalChainProps> = ({ node, ...props }) => {
    return (
        <Box
            borderWidth={2}
            borderColor="border"
            borderStyle="dashed"
            position="relative"
            px="m"
            pb="m"
            pt="l"
            borderRadius="l"
            mx="m"
            maxWidth="full"
            overflowX="auto"
            {...props}
        >
            <WorkflowChainContextProvider parentId={node.id}>
                <Box flex gap="l">
                    {node.branches?.map((branch, index) => (
                        <Box flex column center key={branch.id} mb="xl">
                            <Box
                                flex
                                alignItems="center"
                                mb="s"
                                shrink
                                maxWidth="200px"
                                minWidth="200px"
                            >
                                <Body fontSize="bodyXs" noShrink>
                                    {index === 0 ? 'If' : 'Or if'}
                                </Body>

                                <Box
                                    borderRadius="s"
                                    ml="s"
                                    background="surfaceStrongest"
                                    px="s"
                                    trim
                                >
                                    <Body color="textWeak" fontSize="bodyS">
                                        {branch.name || `Condition ${index + 1}`}
                                    </Body>
                                </Box>
                            </Box>
                            <WorkflowChain
                                items={branch.chain ?? []}
                                showFinishedNode={false}
                                parentId={branch.id}
                                compact={true}
                            >
                                {(branch.chain?.length ?? 0) < 1 && (
                                    <Box flex center justifyContent="center">
                                        <FlowLine
                                            line={{
                                                id: `${node.id}-branch-${branch.id}->placeholder`,
                                                fromItemId: branch.id,
                                            }}
                                            showArrows={false}
                                        />
                                    </Box>
                                )}
                            </WorkflowChain>
                        </Box>
                    ))}

                    <Box flex column center width="200px" minWidth="200px">
                        <Box flex alignItems="center" mb="s">
                            <Box p="2xs" borderRadius="s" mr="s">
                                <Body fontSize="bodyXs">Otherwise</Body>
                            </Box>
                        </Box>
                        <WorkflowChain
                            items={node.else_branch?.chain ?? []}
                            showFinishedNode={false}
                            parentId="else"
                            compact={true}
                        >
                            {(node.else_branch?.chain?.length ?? 0) < 1 && (
                                <Box flex center justifyContent="center">
                                    <FlowLine
                                        line={{
                                            id: `${node.id}-else->placeholder`,
                                            fromItemId: 'else',
                                        }}
                                        showArrows={false}
                                    />
                                </Box>
                            )}
                        </WorkflowChain>
                    </Box>
                </Box>
            </WorkflowChainContextProvider>
        </Box>
    )
}
